import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Send, User, Bot, Square } from 'lucide-react';
import { useCameras, usePersonas } from '@/hooks/queries';
import { useAppStore } from '@/store/app';
import { streamPost } from '@/lib/stream';
export function Chat() {
    const cameras = useCameras();
    const personas = usePersonas();
    const persona = useAppStore((s) => s.persona);
    const setPersona = useAppStore((s) => s.setPersona);
    const [cameraId, setCameraId] = useState('');
    const [draft, setDraft] = useState('');
    const [history, setHistory] = useState([]);
    const [streaming, setStreaming] = useState(false);
    const scroller = useRef(null);
    const ctrlRef = useRef(null);
    const stop = () => {
        ctrlRef.current?.abort();
        ctrlRef.current = null;
        setStreaming(false);
    };
    const send = () => {
        const question = draft.trim();
        if (!question || streaming)
            return;
        if (!cameraId) {
            toast.error('Pick a source first');
            return;
        }
        const userMsg = { role: 'user', content: question };
        const placeholder = { role: 'assistant', content: '' };
        setHistory((h) => [...h, userMsg, placeholder]);
        setDraft('');
        setStreaming(true);
        queueMicrotask(() => scroller.current?.scrollTo({ top: scroller.current.scrollHeight }));
        let acc = '';
        ctrlRef.current = streamPost('/chat/stream', {
            camera_id: cameraId,
            question,
            ...(persona ? { persona } : {}),
        }, {
            onToken: (t) => {
                acc += t;
                setHistory((h) => {
                    const next = h.slice();
                    next[next.length - 1] = { role: 'assistant', content: acc };
                    return next;
                });
                scroller.current?.scrollTo({ top: scroller.current.scrollHeight });
            },
            onDone: (final) => {
                const finalText = final?.answer
                    ?? final?.reply
                    ?? acc;
                setHistory((h) => {
                    const next = h.slice();
                    next[next.length - 1] = { role: 'assistant', content: finalText };
                    return next;
                });
                setStreaming(false);
                ctrlRef.current = null;
            },
            onError: (detail) => {
                toast.error(detail);
                setStreaming(false);
                ctrlRef.current = null;
            },
        });
    };
    return (_jsxs("div", { className: "card", style: { display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)' }, children: [_jsxs("div", { className: "card-header", style: { flexShrink: 0 }, children: [_jsx("span", { className: "card-title", children: "Conversational Q&A" }), _jsxs("div", { style: { display: 'flex', gap: 8 }, children: [_jsxs("select", { value: persona ?? '', onChange: (e) => setPersona(e.target.value || null), children: [_jsx("option", { value: "", children: "Default persona" }), personas.data?.map((p) => (_jsx("option", { value: p.name, children: p.name }, p.name)))] }), _jsxs("select", { value: cameraId, onChange: (e) => setCameraId(e.target.value), children: [_jsx("option", { value: "", children: "No source" }), cameras.data?.map((c) => (_jsx("option", { value: c.camera_id, children: c.name || c.camera_id }, c.camera_id)))] })] })] }), _jsxs("div", { ref: scroller, style: { flex: 1, overflowY: 'auto', padding: 8 }, children: [history.length === 0 && (_jsx("p", { style: { color: 'var(--ey-muted)', fontSize: 13 }, children: "Ask a question about a registered source. Pick a camera above for grounded answers." })), history.map((m, i) => (_jsxs("div", { className: `chat-msg ${m.role}`, style: { display: 'flex', gap: 10, marginBottom: 14 }, children: [_jsx("div", { className: "stat-icon", style: { width: 30, height: 30, background: m.role === 'user' ? 'rgba(255,230,0,.1)' : 'rgba(0,200,150,.15)' }, children: m.role === 'user' ? _jsx(User, { size: 16 }) : _jsx(Bot, { size: 16 }) }), _jsx("div", { style: { flex: 1, whiteSpace: 'pre-wrap', lineHeight: 1.5 }, children: m.content || (streaming && i === history.length - 1 ? _jsx("em", { style: { color: 'var(--ey-muted)' }, children: "thinking\u2026" }) : null) })] }, i)))] }), _jsxs("div", { style: { display: 'flex', gap: 8, paddingTop: 8, borderTop: '1px solid var(--ey-border)' }, children: [_jsx("textarea", { rows: 2, style: { flex: 1, minHeight: 50 }, placeholder: "Ask anything about the registered footage\u2026", value: draft, onChange: (e) => setDraft(e.target.value), onKeyDown: (e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                send();
                            }
                        } }), streaming ? (_jsxs("button", { className: "btn btn-danger", onClick: stop, children: [_jsx(Square, { size: 15 }), " Stop"] })) : (_jsxs("button", { className: "btn btn-primary", onClick: send, children: [_jsx(Send, { size: 15 }), " Send"] }))] })] }));
}
